<template>
    <div>
        <skeleton-loader type="detail" v-if="loading" />
        <div class="pd-screen" v-else-if="program">
            <div class="pd-topbar">
                <v-btn class="pd-back" depressed height="32" @click="goBack">
                    <v-icon size="15" class="mr-1">mdi-arrow-left</v-icon>
                    {{ $t('common.back') }}
                </v-btn>
                <v-spacer />
                <history-controls v-if="canEdit" context="detail" class="mr-2" />
                <actions-menu v-if="isMobile" :items="topbarActions" />
                <template v-else>
                    <v-btn v-if="canEdit" class="btn btn-ghost mr-2" depressed height="36" @click="editProgram">
                        <v-icon size="14" class="mr-1">mdi-pencil</v-icon>
                        {{ $t('common.edit') }}
                    </v-btn>
                    <v-btn class="btn btn-secondary" depressed height="36" @click="assignProgram">
                        <v-icon size="15" class="mr-1">mdi-account-plus</v-icon>
                        {{ $t('programs.assign_program') }}
                    </v-btn>
                </template>
            </div>

            <div class="pd-body">
                <div class="pd-left">
                    <div class="pd-name">{{ program.name }}</div>

                    <div class="pd-stats">
                        <div class="pd-stat">
                            <div class="pd-stat-lbl">{{ $t('common.weeks') }}</div>
                            <div class="pd-stat-val">{{ weeks }}</div>
                        </div>
                        <div class="pd-stat">
                            <div class="pd-stat-lbl">{{ $t('common.trainings') }}</div>
                            <div class="pd-stat-val">{{ routinesQuantity }}</div>
                        </div>
                        <div class="pd-stat">
                            <div class="pd-stat-lbl">{{ $t('common.per_week') }}</div>
                            <div class="pd-stat-val">{{ daysPerWeek }}</div>
                        </div>
                        <div class="pd-stat">
                            <div class="pd-stat-lbl">{{ $t('common.category') }}</div>
                            <div class="pd-stat-val pd-stat-val--sm">{{ programCategory }}</div>
                        </div>
                    </div>

                    <div class="pd-weeknav" v-if="weeks > 1">
                        <v-btn icon small @click="prevWeek"><v-icon>mdi-chevron-left</v-icon></v-btn>
                        <span>{{ $t('common.week') }} {{ currentWeek + 1 }}</span>
                        <v-btn icon small @click="nextWeek"><v-icon>mdi-chevron-right</v-icon></v-btn>
                    </div>

                    <div class="pd-metrics">
                        <div class="pd-metric">
                            <div class="pd-metric-val">{{ metrics.series }}</div>
                            <div class="pd-metric-lbl">{{ $tc('exercises.sets', 2) }}</div>
                            <span class="pd-metric-trend">{{ seriesAvg }} {{ averageLabel }}</span>
                        </div>
                        <div class="pd-metric">
                            <div class="pd-metric-val">{{ metrics.reps }}</div>
                            <div class="pd-metric-lbl">{{ $t('exercises.reps') }}</div>
                            <span class="pd-metric-trend">{{ repsAvg }} {{ averageLabel }}</span>
                        </div>
                        <div class="pd-metric">
                            <div class="pd-metric-val">{{ intensityDisplay }}</div>
                            <div class="pd-metric-lbl">{{ $t('common.intensity') }}</div>
                            <span class="pd-metric-trend">{{ averageLabel }}</span>
                        </div>
                    </div>

                    <load-type-donut :blocks="currentWeekBlocks" />
                </div>

                <div class="pd-right">
                    <detail-blocks-accordion :sections="weekSections" :empty-text="$t('programs.no_routines_added')"
                        :sortable="canEdit" :deletable="canEdit" :duplicable="canEdit"
                        :duplicate-section-tooltip="$t('tooltips.duplicate_week')"
                        :duplicate-item-tooltip="$t('tooltips.copy_routine')"
                        :delete-item-tooltip="$t('tooltips.delete_routine')" @item-click="goToRoutine"
                        @reorder="onReorderTrainings" @delete-item="onDeleteItem"
                        @duplicate-section="onDuplicateWeek" @duplicate-item="onDuplicateRoutine">
                        <template #popup="{ items }">
                            <div v-for="(group, bi) in items" :key="bi" class="rut-popup-block">
                                <exercise-event v-for="(ev, k) in group" :key="k" :event="ev" is-hover-view />
                            </div>
                        </template>
                    </detail-blocks-accordion>
                    <div v-if="canEdit" class="pd-add-actions">
                        <button type="button" class="pd-add-training" @click="openAddTraining">
                            <v-icon size="16" class="mr-1">mdi-plus</v-icon>{{ $t('routines.new_routine') }}
                        </button>
                        <button type="button" class="pd-add-training" @click="openRoutinePicker">
                            <v-icon size="16" class="mr-1">mdi-view-list-outline</v-icon>{{ $t('common.from_library') }}
                        </button>
                    </div>
                </div>
            </div>

            <assign-program-dialog ref="assign" :program-id="programId" :coach-id="coachId" />
            <save-program-dialog ref="saveProgram" edit-mode @success="onProgramSaved" />
            <program-routine-editor ref="editor" @success="onTrainingSaved" />
            <add-existing-routine-dialog ref="routinePicker" :coach-id="coachId" :loading="addingRoutines" @select="onSelectExistingRoutines" />
            <kimsa-confirm ref="confirmDelete" alert-mode :loading="deleting" :close-on-confirm="false" :title="$t('common.delete')"
                :content="$t('common.confirm_delete')" @confirm="doDeleteTraining" @cancel="pendingDelete = null" />
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";
import { db } from "@/utils/Firebase";
import { getMergedBlocks, blockUniqueTime, routineLoadMetrics, clearBlocksAssignedRoutine } from "@/utils/Exercises";
import { randomString } from "@/utils/Common";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import LoadTypeDonut from "@/components/coach/routines/LoadTypeDonut";
import DetailBlocksAccordion from "@/components/coach/routines/DetailBlocksAccordion";
import ExerciseEvent from "@/components/calendar/ExerciseEvent";
import AssignProgramDialog from "@/components/coach/routines/AssignProgramDialog";
import SaveProgramDialog from "@/components/coach/routines/SaveProgramDialog";
import ProgramRoutineEditor from "@/components/coach/routines/ProgramRoutineEditor";
import AddExistingRoutineDialog from "@/components/coach/routines/AddExistingRoutineDialog";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import { BLOCK_COLORS as CALENDAR_BLOCK_COLORS } from "@/utils/BlockColors";
import HistoryControls from "@/components/common/HistoryControls";
import ActionsMenu from "@/components/common/ActionsMenu";
import detailHistoryMixin from "@/mixins/detailHistoryMixin";

export default {
    name: "ProgramDetail",
    components: { SkeletonLoader, LoadTypeDonut, DetailBlocksAccordion, ExerciseEvent, AssignProgramDialog, SaveProgramDialog, ProgramRoutineEditor, AddExistingRoutineDialog, KimsaConfirm, HistoryControls, ActionsMenu },
    mixins: [detailHistoryMixin],
    data() {
        return {
            loading: false,
            program: null,
            currentWeek: 0,
            pendingDelete: null,
            addingRoutines: false,
            duplicating: false,
            deleting: false,
            reorderTimer: null,
            reorderDirty: false,
        }
    },
    computed: {
        ...mapState(['libraryCoach']),
        programId(vm = this) {
            return vm.$route.params.uid || null
        },
        coachId(vm = this) {
            if (vm.isCompany) return vm.program?.coachId || vm.libraryCoach?.id || null
            return vm.currentUser?.uid || null
        },
        isOwner(vm = this) {
            return vm.program?.coachId === vm.currentUser?.uid
        },
        canEdit(vm = this) {
            return vm.isOwner || vm.isCompany
        },
        programImg(vm = this) {
            return vm.program?.img || vm.imgPlaceholder
        },
        assignedRoutines(vm = this) {
            return vm.program?.assignedRoutines || []
        },
        routinesQuantity(vm = this) {
            return vm.assignedRoutines.length
        },
        daysPerWeek(vm = this) {
            return vm.program?.daysPerWeek || 1
        },
        weeks(vm = this) {
            return Math.max(1, Math.ceil(vm.routinesQuantity / vm.daysPerWeek))
        },
        programCategory(vm = this) {
            const cat = vm.program?.category
            if (!cat) return vm.$t('common.not_available_short')
            return vm.routineObjectiveLabel(cat) || cat
        },
        averageLabel(vm = this) {
            return vm.$t('common.average').toLowerCase()
        },
        topbarActions(vm = this) {
            const actions = []
            if (vm.canEdit) actions.push({ key: 'edit', label: vm.$t('common.edit'), icon: 'mdi-pencil-outline', action: vm.editProgram })
            actions.push({ key: 'assign', label: vm.$t('programs.assign_program'), icon: 'mdi-account-plus-outline', action: vm.assignProgram })
            return actions
        },
        weekGroups(vm = this) {
            const perWeek = vm.daysPerWeek
            const groups = []
            for (let i = 0; i < vm.weeks; i++) {
                const index = i * perWeek
                groups.push(vm.assignedRoutines.slice(index, index + perWeek)
                    .map((routine, j) => ({ routine, globalIndex: index + j })))
            }
            return groups
        },
        currentWeekBlocks(vm = this) {
            const group = vm.weekGroups[vm.currentWeek] || []
            const raw = group.flatMap(g => g.routine.blocks || [])
            return blockUniqueTime(JSON.parse(JSON.stringify(raw)))
        },
        metrics(vm = this) {
            return routineLoadMetrics(vm.currentWeekBlocks)
        },
        exercisesCount(vm = this) {
            return vm.metrics.exercisesCount
        },
        seriesAvg(vm = this) {
            return vm.exercisesCount ? (vm.metrics.series / vm.exercisesCount).toFixed(1) : '0'
        },
        repsAvg(vm = this) {
            return vm.metrics.series ? (vm.metrics.reps / vm.metrics.series).toFixed(1) : '0'
        },
        intensityDisplay(vm = this) {
            return vm.metrics.intensity != null ? `${vm.metrics.intensity.toFixed(1)}%` : vm.$t('common.not_available_short')
        },
        weekSections(vm = this) {
            return vm.weekGroups.map((group, i) => ({
                key: i,
                title: `${vm.$t('common.week')} ${i + 1}`,
                count: group.length,
                items: group.map(({ routine, globalIndex }) => {
                    const info = vm.routineInfo(routine)
                    return {
                        key: routine._uid || globalIndex,
                        thumb: routine.img || vm.programImg,
                        name: `${vm.$t('programs.routine_name')} ${globalIndex + 1}`,
                        meta: info.meta,
                        popup: info.popup,
                        clickable: true,
                        raw: { globalIndex },
                    }
                }),
            }))
        },
    },
    async mounted() {
        const vm = this
        await vm.loadProgram()
        vm.startDetailHistory(() => vm.assignedRoutines, vm.applyTrainings)
    },
    async beforeRouteLeave(to, from, next) {
        if (this.reorderDirty) await this.flushReorder()
        next()
    },
    beforeDestroy() {
        if (this.reorderDirty) this.flushReorder()
    },
    methods: {
        goBack() {
            this.$router.back()
        },
        assignProgram() {
            this.$refs.assign.show()
        },
        editProgram() {
            const vm = this
            vm.$refs.saveProgram.show({ ...vm.program, objectID: vm.programId })
        },
        async onProgramSaved() {
            await this.loadProgram()
            this.syncDetailHistory()
        },
        trainingName(index) {
            return `${this.$t('programs.routine_name')} ${index + 1}`
        },
        async applyTrainings(list) {
            const vm = this
            if (!vm.program) return
            vm.program.assignedRoutines = list
            if (vm.currentWeek > 0 && vm.currentWeek >= vm.weeks) vm.currentWeek = vm.weeks - 1
            await vm.persistTrainings(list)
        },
        prevWeek() {
            const vm = this
            vm.currentWeek = vm.currentWeek <= 0 ? vm.weeks - 1 : vm.currentWeek - 1
        },
        nextWeek() {
            const vm = this
            vm.currentWeek = vm.currentWeek >= vm.weeks - 1 ? 0 : vm.currentWeek + 1
        },
        goToRoutine(item) {
            const vm = this
            vm.$router.push({
                name: `${vm.accountType}.routines.program.detail.routine`,
                params: { uid: vm.programId, id: item.raw.globalIndex }
            })
        },
        openAddTraining() {
            const vm = this
            vm.$refs.editor.show({ programId: vm.programId, routineId: vm.assignedRoutines.length, create: true })
        },
        openRoutinePicker() {
            const vm = this
            vm.$refs.routinePicker.show()
        },
        async onSelectExistingRoutines(routines) {
            const vm = this
            vm.addingRoutines = true
            try {
                const snaps = await Promise.all(routines.map(r => db.collection('routines').doc(r.objectID).get()))
                const newTrainings = []
                for (const snap of snaps) {
                    const data = snap.data()
                    if (!data) continue
                    const merged = await getMergedBlocks(data.blocks || [])
                    newTrainings.push({ blocks: merged, _uid: randomString() })
                }
                if (newTrainings.length === 0) return
                const list = [...vm.assignedRoutines, ...newTrainings]
                await vm.persistTrainings(list)
                vm.program.assignedRoutines = list
                vm.commitDetailHistory(vm.$tc('history.add_routines', newTrainings.length, { n: newTrainings.length }))
                vm.$refs.routinePicker?.hide()
            } finally {
                vm.addingRoutines = false
            }
        },
        onTrainingSaved({ routineId, blocks }) {
            const vm = this
            const idx = Number(routineId)
            const list = [...vm.assignedRoutines]
            const isNew = idx >= list.length
            if (isNew) list.push({ blocks, _uid: randomString() })
            else list.splice(idx, 1, { ...list[idx], blocks })
            vm.program.assignedRoutines = list
            vm.commitDetailHistory(isNew ? vm.$t('history.add_routine') : vm.$t('history.edit', { name: vm.trainingName(idx) }))
        },
        onDeleteItem(item) {
            const vm = this
            const idx = item.raw?.globalIndex
            if (idx == null) return
            vm.confirmDeleteTraining(idx)
        },
        cloneTraining(routine) {
            return { ...JSON.parse(JSON.stringify(routine)), _uid: randomString() }
        },
        async insertTrainings(index, trainings) {
            const vm = this
            if (!trainings.length) return
            const list = [...vm.assignedRoutines]
            list.splice(index, 0, ...trainings)
            vm.program.assignedRoutines = list
            await vm.persistTrainings(list)
        },
        async onDuplicateRoutine(item) {
            const vm = this
            const idx = item.raw?.globalIndex
            if (idx == null) return
            const routine = vm.assignedRoutines[idx]
            if (!routine) return
            await vm.withLoading(async () => {
                await vm.insertTrainings(idx + 1, [vm.cloneTraining(routine)])
                vm.commitDetailHistory(vm.$t('history.duplicate', { name: vm.trainingName(idx) }))
            }, 'duplicating')
        },
        async onDuplicateWeek(section, sectionIndex) {
            const vm = this
            const group = vm.weekGroups[sectionIndex] || []
            if (!group.length) return
            const lastIndex = group[group.length - 1].globalIndex
            const copies = group.map(({ routine }) => vm.cloneTraining(routine))
            await vm.withLoading(async () => {
                await vm.insertTrainings(lastIndex + 1, copies)
                vm.commitDetailHistory(vm.$t('history.duplicate', { name: `${vm.$t('common.week')} ${sectionIndex + 1}` }))
            }, 'duplicating')
        },
        onReorderTrainings(flat) {
            const vm = this
            const old = vm.assignedRoutines
            const list = flat.map(item => old[item.raw?.globalIndex]).filter(Boolean)
            if (list.length !== old.length) return
            if (!list.some((routine, i) => routine !== old[i])) return
            vm.program.assignedRoutines = list
            vm.commitDetailHistory(vm.$t('history.reorder_routines'), 'reorder-routines')
            vm.reorderDirty = true
            if (vm.reorderTimer) clearTimeout(vm.reorderTimer)
            vm.reorderTimer = setTimeout(() => vm.flushReorder(), 1200)
        },
        async flushReorder() {
            const vm = this
            if (vm.reorderDirty) await vm.persistTrainings(vm.assignedRoutines)
        },
        confirmDeleteTraining(idx) {
            const vm = this
            vm.pendingDelete = idx
            vm.$refs.confirmDelete.show()
        },
        async doDeleteTraining() {
            const vm = this
            const idx = vm.pendingDelete
            if (idx == null) return
            vm.deleting = true
            try {
                const list = vm.assignedRoutines.filter((_, i) => i !== idx)
                await vm.persistTrainings(list)
                vm.program.assignedRoutines = list
                if (vm.currentWeek > 0 && vm.currentWeek >= vm.weeks) vm.currentWeek = vm.weeks - 1
                vm.pendingDelete = null
                vm.$refs.confirmDelete?.hide()
                vm.commitDetailHistory(vm.$t('history.remove', { name: vm.trainingName(idx) }))
            } finally {
                vm.deleting = false
            }
        },
        async persistTrainings(list) {
            const vm = this
            if (vm.reorderTimer) { clearTimeout(vm.reorderTimer); vm.reorderTimer = null }
            vm.reorderDirty = false
            const assignedRoutines = list.map(routine => {
                const clean = { ...routine, blocks: clearBlocksAssignedRoutine(routine.blocks) }
                delete clean._uid
                return clean
            })
            await db.collection('programs').doc(vm.programId).update({ assignedRoutines })
        },
        routineInfo(routine) {
            const vm = this
            const blocks = blockUniqueTime(JSON.parse(JSON.stringify(routine.blocks || [])))
            const blocksCount = blocks.length
            let minutes = 0
            const popup = []
            blocks.forEach((block, bi) => {
                const color = CALENDAR_BLOCK_COLORS[bi % CALENDAR_BLOCK_COLORS.length]
                const group = []
                block.exercises.forEach(ex => {
                    minutes += ex?.config?.sets?.length || block.sets || 0
                    group.push({
                        event: {
                            ...ex,
                            color,
                            isCircuit: block.isCircuit || false,
                            sets: block.sets || null,
                        }
                    })
                })
                if (group.length) popup.push(group)
            })
            const added = Number(minutes * 1.3).toFixed()
            const range = Number(minutes) === Number(added) ? `${minutes}` : `${minutes}–${added}`
            const meta = `${blocksCount} ${vm.$tc('common.blocks', blocksCount).toLowerCase()} · ${range} ${vm.$tc('common.minutes', 2).toLowerCase()}`
            return { meta, popup }
        },
        async loadProgram() {
            const vm = this
            vm.loading = true
            const programRef = await db.collection('programs').doc(vm.programId).get()
            vm.program = programRef.data()
            for (let routine of (vm.program?.assignedRoutines || [])) {
                routine.blocks = await getMergedBlocks(routine.blocks)
                routine._uid = randomString()
            }
            vm.loading = false
        },
    },
}
</script>

<style scoped>
.pd-screen {
    display: flex;
    flex-direction: column;
}

.pd-topbar {
    display: flex;
    align-items: center;
    height: 56px;
    flex-shrink: 0;
    padding: 0 16px;
    border-top: 1px solid var(--v-outline-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.pd-back {
    border-radius: 7px !important;
    border: 1px solid var(--v-outline2-base) !important;
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    font-size: 13px;
    font-weight: 500;
    text-transform: none !important;
    letter-spacing: normal !important;
    padding: 0 12px !important;
}

.pd-back:hover {
    color: var(--v-txtPrimary-base) !important;
}

.btn {
    text-transform: none !important;
    letter-spacing: normal !important;
    font-weight: 700;
    border-radius: 8px !important;
    min-width: auto !important;
    padding: 0 16px !important;
}

.btn-ghost {
    background: var(--v-surface2-base) !important;
    color: var(--v-txtSecondary-base) !important;
    border: 1px solid var(--v-outline-base) !important;
}

.btn-ghost:hover {
    color: var(--v-txtPrimary-base) !important;
    border-color: var(--v-outline2-base) !important;
}

.btn-secondary {
    background: linear-gradient(135deg, rgba(204, 255, 51, .15), rgba(51, 153, 153, .15)) !important;
    color: var(--v-primary-base) !important;
    border: 1.5px solid rgba(204, 255, 51, .5) !important;
}

.theme--light .btn-secondary {
    color: var(--v-accent-base) !important;
    border-color: rgba(51, 153, 153, .4) !important;
}

.pd-body {
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 0;
}

.pd-left {
    padding: 20px 18px;
    border-right: 1px solid var(--v-outline-base);
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.pd-name {
    font-size: 16px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
    letter-spacing: -.01em;
}

.pd-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
}

.pd-stat {
    padding: 10px 8px;
    text-align: center;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
}

.pd-stat-lbl {
    font-size: 9px;
    color: var(--v-txtSecondary-base);
    font-weight: 600;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: .04em;
}

.pd-stat-val {
    font-size: 15px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.pd-stat-val--sm {
    font-size: 11px;
}

.pd-weeknav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--v-primary-base);
    border-radius: 8px;
    padding: 2px 8px;
}

.pd-weeknav span {
    font-size: 13px;
    font-weight: 700;
    color: #0C0E12;
}

.pd-weeknav .v-btn {
    color: #0C0E12 !important;
}

.pd-weeknav ::v-deep .v-icon {
    color: #0C0E12 !important;
}

.pd-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.pd-metric {
    padding: 12px 8px;
    text-align: center;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
}

.pd-metric-val {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    line-height: 1;
}

.pd-metric-lbl {
    font-size: 9px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-transform: uppercase;
    letter-spacing: .05em;
    margin: 4px 0 6px;
}

.pd-metric-trend {
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(0, 153, 102, .12);
    color: var(--v-success-base);
}

.pd-right {
    padding: 16px;
}

.pd-add-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    margin-top: 8px;
}

.pd-add-training {
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed var(--v-outline2-base);
    border-radius: 12px;
    background: none;
    color: var(--v-txtSecondary-base);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .15s;
}

@media (max-width: 600px) {
    .pd-add-actions {
        grid-template-columns: 1fr;
    }
}

.pd-add-training:hover {
    color: var(--v-txtPrimary-base);
}

.theme--dark .pd-add-training:hover {
    border-color: var(--v-primary-base);
}

.theme--light .pd-add-training:hover {
    border-color: var(--v-accent-base);
}

.pd-add-training .v-icon {
    color: inherit !important;
}

@media (min-width: 961px) {
    .pd-screen {
        height: calc(100vh - 64px);
        overflow: hidden;
    }

    .pd-body {
        flex: 1;
        min-height: 0;
        grid-template-rows: minmax(0, 1fr);
    }

    .pd-left {
        min-height: 0;
        overflow-y: auto;
    }

    .pd-right {
        min-height: 0;
        overflow-y: auto;
    }
}

@media (max-width: 960px) {
    .pd-body {
        grid-template-columns: 1fr;
    }

    .pd-left {
        border-right: none;
        border-bottom: 1px solid var(--v-outline-base);
    }
}

@media (max-width: 600px) {
    .pd-stats {
        grid-template-columns: 1fr 1fr;
    }
}

.rut-popup-block {
    padding: 4px 0px;
    border-bottom: 1px solid var(--v-outline-base);
}

.rut-popup-block:last-child {
    border-bottom: none;
}

.rut-popup-block ::v-deep .exercise-item {
    border-bottom: none;
}
</style>
